import {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";

import { DEVICE_ACTIONS, DialogProps } from "components/Actions/types";
import Autocomplete from "components/DesignComponents/Autocomplete";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import ListChoice from "components/DesignComponents/ListChoice";
import HighlightedOption from "components/HighlightedOption";
import ParamsContext from "context/params/paramsContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { useStringInputValidation } from "shared/hooks/useStringInputValidation";
import {
    DROPDOWN_SIZES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    FILE_ID_TYPE,
    TABLE_NAMES,
    TRI_SOURCES
} from "shared/types/enums";
import { BackgroundActionData } from "shared/types/interfaces";

import DialogActions from "../../components/DialogActions";
import TriSource from "../../components/TriSource/TriSource";
import DeviceRadioGroup from "../components/DeviceRadioGroup";
import { useTriSourceAction } from "../hooks";
import { EXPORT_TYPE } from "../types";

const ExportDeviceData = ({ isOpen, close }: DialogProps) => {
    const { setReloadItems } = useContext(ParamsContext);

    const { cancelSource, isCanceled, getData } = useApi();

    const {
        t,
        columns,
        activeSource,
        sourcePayload,
        isSourceReady,
        setBackgroundActionId,
        deselectAllRows,
        postData,
        handleResponse
    } = useTriSourceAction();

    const isMounted = useIsMounted();

    const [isLoading, setIsLoading] = useState(false);
    const [isPropertiesLoading, setIsPropertiesLoading] = useState(true);
    const [target, setTarget] = useState(EXPORT_TYPE.CSV);
    const [deviceIdType, setDeviceIdType] = useState(FILE_ID_TYPE.Imei);
    const [propertiesInputValue, setPropertiesInputValue] = useState("");
    const [properties, setProperties] = useState<string[]>([]);
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [description, setDescription] = useState("");

    const { stringInputError } = useStringInputValidation(description);

    useEffect(() => {
        const getProperties = async () => {
            try {
                setIsPropertiesLoading(true);

                const { data }: { data: string[] } = await getData(
                    `${ENDPOINTS.DeviceExport}${ENDPOINT_PATH_SEGMENTS.Properties}`
                );

                if (isMounted) {
                    const activeColumns = columns
                        .filter(({ isActive }) => isActive)
                        .map(({ resource }) => resource as string);

                    const initiallySelected = data.filter(property =>
                        activeColumns.includes(property)
                    );

                    setProperties(data);
                    setSelectedProperties(initiallySelected);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                }
            }

            setIsPropertiesLoading(false);
        };

        isOpen && getProperties();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };
        // eslint-disable-next-line
    }, [isOpen]);

    const isReadyToConfirm =
        isSourceReady &&
        sourcePayload !== null &&
        stringInputError === null &&
        selectedProperties.length > 0;

    const handleCloseDialog = useCallback(() => {
        close(DEVICE_ACTIONS.ExportDeviceData);
    }, [close]);

    const handleTarget = (e: any) => setTarget(e.target.value);

    const getExportData = useCallback(() => {
        const formData = new FormData();

        formData.append("source", activeSource);
        formData.append("id_type", deviceIdType);
        formData.append(activeSource, sourcePayload as Blob);
        formData.append("format", target);
        formData.append("description", description);

        selectedProperties.forEach(property =>
            formData.append("columns[]", property)
        );

        return activeSource === TRI_SOURCES.FromFile
            ? formData
            : {
                  source: activeSource,
                  [activeSource]: sourcePayload,
                  format: target,
                  description,
                  columns: selectedProperties
              };
    }, [
        sourcePayload,
        target,
        activeSource,
        selectedProperties,
        deviceIdType,
        description
    ]);

    const handleDeviceIdChange = (e: { target: { value: FILE_ID_TYPE } }) => {
        const { value } = e.target;
        setDeviceIdType(value);
    };

    const handleDescription = (e: ChangeEvent<HTMLInputElement>) =>
        setDescription(e.target.value);

    const handleSubmit = useCallback(async () => {
        try {
            setIsLoading(true);

            const { data }: { data: BackgroundActionData } = await postData(
                ENDPOINTS.DeviceExport,
                getExportData()
            );

            setBackgroundActionId(
                data,
                TABLE_NAMES.Devices,
                () => {
                    deselectAllRows();
                    handleCloseDialog();
                    setReloadItems(TABLE_NAMES.Devices);
                },
                () => setIsLoading(false)
            );
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    }, [
        getExportData,
        deselectAllRows,
        handleCloseDialog,
        setReloadItems,
        handleResponse,
        setBackgroundActionId,
        postData
    ]);

    return (
        <TriSource
            data-testid="export-device-data-dialog-with-tri-source"
            title={t("Dialog##export device data")}
            description={t("Dialog##export device description")}
            confirmationDescription={t(
                "Dialog##confirmations##export device data confirmation"
            )}
            close={handleCloseDialog}
            isBulkActionLoading={isLoading}
            open={isOpen}
            submit={handleSubmit}
            isLoading={isLoading}
            slotProps={{
                transition: {
                    onExited: () => {
                        setIsLoading(false);
                        setIsPropertiesLoading(false);
                        setTarget(EXPORT_TYPE.CSV);
                        setDeviceIdType(FILE_ID_TYPE.Imei);
                        setProperties([]);
                        setSelectedProperties([]);
                        setDescription("");
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    confirmationButtonText={t("Button##export")}
                    loaderText={t("Button##exporting")}
                    isReadyToConfirm={isReadyToConfirm}
                />
            }
            deviceIdentifier={
                <DeviceRadioGroup
                    title={t("Dialog##device identifier")}
                    target={deviceIdType}
                    firstRadioLabel={t("Dialog##imei")}
                    firstRadioValue={FILE_ID_TYPE.Imei}
                    secondRadioLabel={t("Dialog##sn")}
                    secondRadioValue={FILE_ID_TYPE.SerialId}
                    isLoading={isLoading}
                    onChange={handleDeviceIdChange}
                />
            }
        >
            <Autocomplete
                data-testid="export-device-select-data"
                isRequired
                multiple
                size={DROPDOWN_SIZES.Medium}
                labelLeft={t("Device export##select data")}
                options={properties}
                value={selectedProperties}
                loading={isPropertiesLoading}
                readOnly={isPropertiesLoading || isLoading}
                textFieldParams={{
                    placeholder:
                        selectedProperties.length === 0
                            ? t("Dialog##select")
                            : ""
                }}
                getOptionLabel={option =>
                    t(`Device export##properties##${option}`)
                }
                renderOption={(propsData, option, state) => {
                    const { key, ...restProps } = propsData;

                    return (
                        <ListChoice
                            key={key}
                            title={
                                <HighlightedOption
                                    text={t(
                                        `Device export##properties##${option}`
                                    )}
                                    query={propertiesInputValue}
                                />
                            }
                            isCheckbox
                            selected={state.selected}
                            {...restProps}
                        />
                    );
                }}
                onInputChange={(_, newInputValue) =>
                    setPropertiesInputValue(newInputValue)
                }
                onChange={(_, property) => setSelectedProperties(property)}
                customStyle={{ marginBottom: "16px" }}
            />

            <DeviceRadioGroup
                title={t("Dialog##export file format")}
                target={target}
                firstRadioLabel={EXPORT_TYPE.CSV.toUpperCase()}
                firstRadioValue={EXPORT_TYPE.CSV}
                secondRadioLabel={EXPORT_TYPE.XLSX.toUpperCase()}
                secondRadioValue={EXPORT_TYPE.XLSX}
                isLoading={isLoading}
                onChange={handleTarget}
            />

            <InputField
                fullWidth
                labelLeft={`${t("Dialog##description")} (${t("Dialog##optional")})`}
                placeholder={t("General##enter here")}
                size={INPUT_FIELD_SIZES.Medium}
                errorText={stringInputError}
                readOnly={isLoading}
                onChange={handleDescription}
                customStyle={{ marginBottom: "16px" }}
            />
        </TriSource>
    );
};

export default ExportDeviceData;
